import { map } from "rxjs";
import { DBFactory } from "~/classes/database/db_factory";
import { UserPermission, UserPermissions } from "./user-permissions.model";
import { UserRole } from "./user-roles.model";

export class PermissionsService {
  static streamUserRoles() {
    const db = DBFactory.createDatabase();
    return db
      .streamList({
        path: "/userRoles",
        collection: "/userRoles",
      })
      .pipe(
        map((userRoles) =>
          userRoles.map((userRole) => UserRole.fromMap(userRole))
        )
      );
  }

  static async fetchUserRole({
    roleId,
  }: {
    roleId: string;
  }): Promise<UserRole> {
    const db = DBFactory.createDatabase();
    const data = await db.get({
      path: `userRoles/${roleId}`,
      collection: "/userRoles",
    });

    return UserRole.fromMap(data);
  }

  static streamUserPermissions() {
    const db = DBFactory.createDatabase();
    return db
      .streamList({
        path: "/userPermissions",
        collection: "/userPermissions",
      })
      .pipe(
        map((userPermissions) =>
          userPermissions.map((userPermission) =>
            UserPermission.fromMap(userPermission)
          )
        )
      );
  }

  static async fetchUserPermission({
    permissionId,
  }: {
    permissionId: string;
  }): Promise<UserPermission> {
    const db = DBFactory.createDatabase();
    const data = await db.get({
      path: `userPermissions/${permissionId}`,
      collection: "/userPermissions",
    });

    return UserPermission.fromMap(data);
  }

  static async saveUserPermissions({
    userPermissions,
  }: {
    userPermissions: UserPermissions;
  }) {
    const db = DBFactory.createDatabase();
    await db.batchUpdate(userPermissions);
  }

  static async fetchUserCustomClaims({
    permissions,
  }: {
    permissions?: string[];
  }): Promise<CustomClaims[]> {
    const db = DBFactory.createDatabase();

    if (!permissions || permissions.length === 0) {
      const data = await db.list({
        path: "/userCustomClaims",
        collection: "/userCustomClaims",
      });

      return data as CustomClaims[];
    }

    const queryPromises = [];

    // Split permissions into chunks of 10
    const chunkSize = 10;
    for (let i = 0; i < permissions.length; i += chunkSize) {
      const chunk = permissions.slice(i, i + chunkSize);
      const query = [] as ModelQueryConfig;

      query.push({
        field: "permissions",
        type: "where",
        operator: "array-contains-any",
        value: chunk,
      });

      const userPermissionsPromise = db.list(
        {
          path: "/userCustomClaims",
          collection: "/userCustomClaims",
        },
        query
      );

      queryPromises.push(userPermissionsPromise);
    }

    // Execute all queries in parallel
    const results = await Promise.all(queryPromises);

    // Combine results and remove duplicates
    const combinedResults = [];

    for (const claimsArray of results) {
      for (const claims of claimsArray) {
        const customClaim = claims as CustomClaims;

        combinedResults.push(customClaim);
      }
    }

    return combinedResults;
  }

  static streamUserCustomClaims({ userId }: { userId: string }) {
    const db = DBFactory.createDatabase();
    return db
      .streamRecord({
        path: `/userCustomClaims/${userId}`,
        collection: "/userCustomClaims",
      })
      .pipe(map((data) => data as DBCustomClaims));
  }

  static async getUserCustomClaims({
    userId,
  }: {
    userId: string;
  }): Promise<DBCustomClaims> {
    const db = DBFactory.createDatabase();
    const data = await db.get({
      path: `userCustomClaims/${userId}`,
      collection: "/userCustomClaims",
    });

    return data as DBCustomClaims;
  }
}
